import { useEffect, useState } from "react";
import { Star, X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/** StarRow — pure presentational; emits the picked rating via onChange.
 * 5 stars, large hit-target (touch-friendly). Empty / filled state via
 * Lucide's ``Star`` with the ``fill`` attribute. */
function StarRow({ value, onChange, size = 32, testid }) {
    const [hover, setHover] = useState(0);
    const shown = hover || value || 0;
    return (
        <div className="flex items-center gap-1" data-testid={testid}>
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    data-testid={`${testid}-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => onChange(n)}
                    className="p-0.5 transition-transform hover:scale-110"
                    aria-label={`${n} star`}
                >
                    <Star
                        size={size}
                        className={n <= shown ? "text-amber-400" : "text-muted-foreground"}
                        fill={n <= shown ? "currentColor" : "none"}
                        strokeWidth={2}
                    />
                </button>
            ))}
        </div>
    );
}


/** ReviewModal — opened from the Order Track page once status=DELIVERED.
 *
 * Props:
 * - orderId: string (required)
 * - hasRider: boolean — when true, the rider subsection is rendered
 * - onClose: () => void
 * - onSubmitted: () => void — fired after a successful POST
 *
 * Submits to ``POST /api/customer/orders/{oid}/review``. Pre-loads the
 * existing review (if any) so the modal behaves like a PATCH the second
 * time it's opened.
 */
export default function ReviewModal({ orderId, hasRider, onClose, onSubmitted }) {
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [overall, setOverall] = useState(0);
    const [overallComment, setOverallComment] = useState("");
    const [items, setItems] = useState([]);              // [{product_id, name, rating, comment}]
    const [riderRating, setRiderRating] = useState(0);
    const [riderComment, setRiderComment] = useState("");
    const [existing, setExisting] = useState(null);
    const [showItemSection, setShowItemSection] = useState(false);
    const [showRiderSection, setShowRiderSection] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/orders/${orderId}/review`);
                if (cancelled) return;
                const r = data.review;
                const ord = data.order || {};
                // Seed items from the order so the customer sees each line.
                const seeded = (ord.items || []).map((it) => ({
                    product_id: it.product_id,
                    name: it.name,
                    rating: 0,
                    comment: "",
                }));
                if (r) {
                    setExisting(r);
                    setOverall(r.overall_rating || 0);
                    setOverallComment(r.overall_comment || "");
                    setRiderRating(r.rider_rating || 0);
                    setRiderComment(r.rider_comment || "");
                    // Merge any saved per-item ratings on top of the seed list.
                    const byPid = Object.fromEntries((r.item_ratings || []).map((x) => [x.product_id, x]));
                    setItems(seeded.map((s) => byPid[s.product_id]
                        ? { ...s, rating: byPid[s.product_id].rating, comment: byPid[s.product_id].comment || "" }
                        : s));
                    if ((r.item_ratings || []).length > 0) setShowItemSection(true);
                    if (r.rider_rating) setShowRiderSection(true);
                } else {
                    setItems(seeded);
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not load order");
                onClose?.();
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orderId, onClose]);

    const submit = async () => {
        if (!overall) {
            toast.error("Please pick an overall rating.");
            return;
        }
        setBusy(true);
        try {
            // Only send item_ratings with a non-zero rating; rider only when set.
            const itemPayload = items
                .filter((it) => it.rating > 0)
                .map((it) => ({
                    product_id: it.product_id,
                    rating: it.rating,
                    comment: it.comment || null,
                }));
            const body = {
                overall_rating: overall,
                overall_comment: overallComment || null,
                item_ratings: itemPayload,
                rider_rating: hasRider && riderRating > 0 ? riderRating : null,
                rider_comment: hasRider && riderRating > 0 ? (riderComment || null) : null,
            };
            await api.post(`/orders/${orderId}/review`, body);
            toast.success(existing ? "Review updated" : "Thanks for your review!");
            onSubmitted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit review");
        } finally { setBusy(false); }
    };

    const updateItem = (pid, patch) => {
        setItems((prev) => prev.map((it) => it.product_id === pid ? { ...it, ...patch } : it));
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
            data-testid="review-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="bg-card w-full sm:max-w-md md:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto">
                <div className="px-5 py-4 flex items-center justify-between border-b border-border sticky top-0 bg-card z-10">
                    <h2 className="font-bold text-lg">{existing ? "Edit your review" : "Rate your order"}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="review-close"
                        className="p-1 -mr-1 text-muted-foreground hover:text-foreground"
                    ><X size={20} /></button>
                </div>

                {loading ? (
                    <div className="p-12 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="animate-spin" size={20} />
                    </div>
                ) : (
                    <div className="px-5 py-4 space-y-5">
                        {/* Overall — mandatory */}
                        <section data-testid="review-overall">
                            <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1.5">
                                Overall experience <span className="text-rose-500">*</span>
                            </p>
                            <StarRow value={overall} onChange={setOverall} testid="overall-stars" />
                            <textarea
                                data-testid="overall-comment"
                                value={overallComment}
                                onChange={(e) => setOverallComment(e.target.value)}
                                placeholder="Tell us how it went (optional, private to admins)"
                                rows={2}
                                className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            />
                        </section>

                        {/* Per-item — optional, collapsible */}
                        <section data-testid="review-items">
                            <button
                                type="button"
                                onClick={() => setShowItemSection((x) => !x)}
                                data-testid="toggle-items"
                                className="text-xs font-bold text-primary hover:underline"
                            >{showItemSection ? "− Hide" : "+ Rate individual items"} ({items.length})</button>
                            {showItemSection && (
                                <div className="mt-3 space-y-3">
                                    {items.map((it) => (
                                        <div key={it.product_id} className="bg-muted/40 rounded-xl p-3"
                                             data-testid={`item-${it.product_id}`}>
                                            <p className="text-sm font-semibold mb-1.5 line-clamp-1">{it.name}</p>
                                            <StarRow
                                                value={it.rating}
                                                onChange={(v) => updateItem(it.product_id, { rating: v })}
                                                size={22}
                                                testid={`item-stars-${it.product_id}`}
                                            />
                                            {it.rating > 0 && (
                                                <input
                                                    type="text"
                                                    data-testid={`item-comment-${it.product_id}`}
                                                    value={it.comment}
                                                    onChange={(e) => updateItem(it.product_id, { comment: e.target.value })}
                                                    placeholder="Optional note"
                                                    className="mt-2 w-full px-2 py-1.5 rounded-md border border-border bg-card text-xs"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Rider — optional, collapsible (only when an in-app rider was assigned) */}
                        {hasRider && (
                            <section data-testid="review-rider">
                                <button
                                    type="button"
                                    onClick={() => setShowRiderSection((x) => !x)}
                                    data-testid="toggle-rider"
                                    className="text-xs font-bold text-primary hover:underline"
                                >{showRiderSection ? "− Hide" : "+ Rate the rider"}</button>
                                {showRiderSection && (
                                    <div className="mt-3 bg-muted/40 rounded-xl p-3">
                                        <StarRow value={riderRating} onChange={setRiderRating} size={22} testid="rider-stars" />
                                        {riderRating > 0 && (
                                            <textarea
                                                data-testid="rider-comment"
                                                value={riderComment}
                                                onChange={(e) => setRiderComment(e.target.value)}
                                                placeholder="Anything the rider should know?"
                                                rows={2}
                                                className="mt-2 w-full px-2 py-1.5 rounded-md border border-border bg-card text-xs"
                                            />
                                        )}
                                    </div>
                                )}
                            </section>
                        )}

                        <p className="text-[10px] text-muted-foreground">
                            Your written notes stay private to the store admin. Only the star rating contributes to the public average.
                        </p>

                        <button
                            type="button"
                            data-testid="review-submit"
                            disabled={busy || !overall}
                            onClick={submit}
                            className="w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="animate-spin" size={16} />
                                  : <CheckCircle2 size={16} />}
                            {existing ? "Update review" : "Submit review"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
